import type { ComponentProps, ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Every small label on the page: audience chips, roadmap chips, filing status
 * tabs, the "READY TO SIGN" marker.
 *
 * Two shapes, per the brand board. A `pill` is fully round. A `tab` is a
 * folder tab — square on top, flat on the bottom — which only reads correctly
 * when it butts against something: give the row `items-end` and the tab
 * `-mb-px` so its open edge merges into the rule below it.
 *
 * Every tone is a complete literal string rather than an interpolation.
 * app.css declares `@source` explicitly, so a constructed `bg-brand-${tone}`
 * is never seen by the scanner and the utility is never generated.
 */
const toneClass = {
    default: 'border-border bg-card text-foreground',
    quiet: 'border-border bg-card text-muted-foreground',
    sageSolid: 'border-brand-sage bg-brand-sage text-brand-ink',
    sage: 'border-brand-sage/40 bg-brand-sage/20 text-foreground',
    oxblood: 'border-brand-oxblood/30 bg-card text-brand-oxblood',
} as const;

export function Chip({
    children,
    className,
    dot = false,
    shape = 'pill',
    tone = 'default',
    ...props
}: ComponentProps<'span'> & {
    shape?: 'pill' | 'tab';
    tone?: keyof typeof toneClass;
    dot?: boolean;
    children: ReactNode;
}) {
    return (
        <span
            {...props}
            className={cn(
                'inline-flex items-center gap-2 border px-3 py-[7px] font-mono text-[12px] leading-none tracking-[0.06em] whitespace-nowrap uppercase',
                shape === 'tab'
                    ? 'rounded-t-[8px] rounded-b-none'
                    : 'rounded-full',
                toneClass[tone],
                className,
            )}
        >
            {dot ? (
                <span
                    aria-hidden="true"
                    className="size-[6px] shrink-0 rounded-full bg-brand-sage"
                />
            ) : null}
            {children}
        </span>
    );
}
